import {
  type EnvelopeFields,
  signEnvelope,
  verifyEnvelopeOrThrow,
  ED25519_SIG_BYTES,
} from "../sender-signatures/SenderSignatures";
export type { EnvelopeFields } from "../sender-signatures/SenderSignatures";

// Wire-format version tags.
//   v1 — legacy, unsigned; still decoded for back-compat, never produced by
//        this client.
//   v2 — includes a 64-byte Ed25519 signature (see sender-signatures/).
const ENVELOPE_PREFIX = "kaether:e2ee:v1:";
const ENVELOPE_SIGNED_PREFIX = "kaether:e2ee:v2:";
const GROUP_PREFIX = "kaether:e2ee:v1:grp:";

type EnvelopeKind = "pk" | "msg";

export interface DecodedEnvelope {
  kind: EnvelopeKind;
  senderDeviceId: number;
  ciphertext: Uint8Array;
  /** Present on v2 (signed) envelopes. Absent on legacy v1 envelopes. */
  signature?: Uint8Array;
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(str: string): Uint8Array {
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4 !== 0) base64 += "=";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Encode a signed (v2) envelope.
 *
 * Wire format: `kaether:e2ee:v2:<kind>:<senderDeviceId>:<b64url(ciphertext)>:<b64url(signature)>`
 */
export function encodeSignedEnvelope(
  kind: EnvelopeKind,
  senderDeviceId: number,
  ciphertext: Uint8Array,
  signature: Uint8Array,
): string {
  return (
    `${ENVELOPE_SIGNED_PREFIX}${kind}:${senderDeviceId}:` +
    `${toBase64Url(ciphertext)}:${toBase64Url(signature)}`
  );
}

/**
 * Sign and encode a v2 envelope in one step.
 */
export async function encodeAndSignEnvelope(
  kind: EnvelopeKind,
  fields: EnvelopeFields,
  privateKey: CryptoKey,
): Promise<string> {
  const sig = await signEnvelope(fields, privateKey);
  return encodeSignedEnvelope(kind, fields.sender.deviceId, fields.ciphertext, sig);
}

/**
 * Legacy v1 encoder — kept for back-compat with clients that have not yet
 * upgraded. New code should use `encodeAndSignEnvelope`.
 *
 * @deprecated Produces unsigned envelopes.
 */
export function encodeEnvelope(
  kind: EnvelopeKind,
  senderDeviceId: number,
  ciphertext: Uint8Array,
): string {
  return `${ENVELOPE_PREFIX}${kind}:${senderDeviceId}:${toBase64Url(ciphertext)}`;
}

/**
 * Decode either a v1 (legacy, unsigned) or v2 (signed) envelope string.
 *
 * For v2 envelopes the `signature` field is populated; callers should
 * immediately call `verifyEnvelopeSignature` before decrypting.
 */
export function decodeEnvelope(text: string): DecodedEnvelope | null {
  // ── v2 (signed) ───────────────────────────────────────────────────────────
  if (text.startsWith(ENVELOPE_SIGNED_PREFIX)) {
    const rest = text.slice(ENVELOPE_SIGNED_PREFIX.length);
    // kind : deviceId : b64ciphertext : b64signature
    const colonIdx1 = rest.indexOf(":");
    const colonIdx2 = rest.indexOf(":", colonIdx1 + 1);
    const colonIdx3 = rest.lastIndexOf(":");
    if (colonIdx1 < 0 || colonIdx2 < 0 || colonIdx3 <= colonIdx2) return null;

    const kindStr = rest.slice(0, colonIdx1);
    if (kindStr !== "pk" && kindStr !== "msg") return null;

    const deviceIdStr = rest.slice(colonIdx1 + 1, colonIdx2);
    const senderDeviceId = parseInt(deviceIdStr, 10);
    if (isNaN(senderDeviceId)) return null;

    const b64Ciphertext = rest.slice(colonIdx2 + 1, colonIdx3);
    const b64Sig = rest.slice(colonIdx3 + 1);

    try {
      const ciphertext = fromBase64Url(b64Ciphertext);
      const signature = fromBase64Url(b64Sig);
      if (signature.byteLength !== ED25519_SIG_BYTES) return null;
      return { kind: kindStr, senderDeviceId, ciphertext, signature };
    } catch {
      return null;
    }
  }

  // ── v1 (legacy, unsigned) ─────────────────────────────────────────────────
  if (!text.startsWith(ENVELOPE_PREFIX)) return null;

  const rest = text.slice(ENVELOPE_PREFIX.length);
  const parts = rest.split(":");
  if (parts.length !== 3) return null;

  const [kindStr, deviceIdStr, b64] = parts;
  if (kindStr !== "pk" && kindStr !== "msg") return null;

  const senderDeviceId = parseInt(deviceIdStr, 10);
  if (isNaN(senderDeviceId)) return null;

  try {
    const ciphertext = fromBase64Url(b64);
    return { kind: kindStr, senderDeviceId, ciphertext };
  } catch {
    return null;
  }
}

/**
 * Verify the sender signature embedded in a decoded v2 envelope.
 *
 * Throws if the signature is invalid or absent (i.e. legacy v1 envelope).
 */
export async function verifyEnvelopeSignature(
  envelope: DecodedEnvelope,
  fields: EnvelopeFields,
  senderPublicKey: CryptoKey,
): Promise<void> {
  if (!envelope.signature) {
    throw new Error(
      "[envelope] Cannot verify signature: envelope has no signature field (legacy v1 envelope)",
    );
  }
  await verifyEnvelopeOrThrow(fields, envelope.signature, senderPublicKey);
}

export function isE2EEEnvelope(text: string): boolean {
  return text.startsWith(ENVELOPE_PREFIX) || text.startsWith(ENVELOPE_SIGNED_PREFIX);
}

export function isGroupEnvelope(text: string): boolean {
  return text.startsWith(GROUP_PREFIX);
}

export function encodeGroupEnvelope(
  recipients: Record<string, string>,
): string {
  const json = JSON.stringify(recipients);
  const bytes = new TextEncoder().encode(json);
  return `${GROUP_PREFIX}${toBase64Url(bytes)}`;
}

export function decodeGroupEnvelope(
  text: string,
): Record<string, string> | null {
  if (!text.startsWith(GROUP_PREFIX)) return null;

  const b64 = text.slice(GROUP_PREFIX.length);
  try {
    const bytes = fromBase64Url(b64);
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch {
    return null;
  }
}
